import { unzip, zip, type Unzipped, type Zippable } from 'fflate';
import type { AttachmentMimeType, Uuid } from '@/data/db';

/**
 * 完整備份的 ZIP 容器（spec 20.2、data-model 10.1）。
 *
 * 圖片**不做 Base64 內嵌**：那會讓檔案膨脹三分之一，
 * 而且匯出時要在記憶體裡組出一個超大字串。
 * ZIP 用 store（不壓縮）等級，圖片本身已經是壓縮格式，重壓只是浪費時間。
 */

export const DATA_JSON_PATH = 'data.json';
export const IMAGES_PREFIX = 'images/';

export class ZipError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ZipError';
  }
}

const EXTENSIONS: Record<AttachmentMimeType, string> = {
  'image/webp': 'webp',
  'image/png': 'png',
  'image/jpeg': 'jpg',
};

export function attachmentFileName(id: Uuid, mimeType: AttachmentMimeType): string {
  return `${IMAGES_PREFIX}${id}.${EXTENSIONS[mimeType]}`;
}

export interface ZipEntry {
  path: string;
  bytes: Uint8Array;
}

export async function packBackupZip(
  dataJson: string,
  images: readonly ZipEntry[],
): Promise<Uint8Array> {
  const payload: Zippable = {
    // data.json 是文字，值得壓；圖片不壓。
    [DATA_JSON_PATH]: [new TextEncoder().encode(dataJson), { level: 6 }],
  };

  for (const image of images) {
    payload[image.path] = [image.bytes, { level: 0 }];
  }

  return new Promise((resolve, reject) => {
    zip(payload, (error, data) => {
      if (error) reject(new ZipError(`打包備份失敗：${error.message}`));
      else resolve(data);
    });
  });
}

export interface UnpackedBackup {
  dataJson: string;
  images: Map<string, Uint8Array>;
}

export async function unpackBackupZip(archive: Uint8Array): Promise<UnpackedBackup> {
  const entries = await new Promise<Unzipped>((resolve, reject) => {
    unzip(archive, (error, data) => {
      if (error) reject(new ZipError('這個 ZIP 讀不起來，沒有動到任何資料。'));
      else resolve(data);
    });
  });

  const raw = entries[DATA_JSON_PATH];
  if (!raw) {
    throw new ZipError('這個 ZIP 裡面沒有 data.json，不是 GentleTask 的完整備份。');
  }

  const images = new Map<string, Uint8Array>();
  for (const [path, bytes] of Object.entries(entries)) {
    if (path.startsWith(IMAGES_PREFIX) && bytes.length > 0) images.set(path, bytes);
  }

  return { dataJson: new TextDecoder().decode(raw), images };
}
